import { meanUserId } from "@lib/constants";
import { mvfIsMeaningfullyValued } from "@lib/mvfFinality";
import { type MVF } from "@lib/tranche";
import type { Evaluator } from "@lib/types";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import {
  deriveLegacyProposalFormId,
  deriveLegacyProposalId,
} from "@lib/utils/idGeneration";
import { validatePoints } from "@lib/validatePoints";
import includes from "lodash/includes";
import isArray from "lodash/isArray";
import isEqualWith from "lodash/isEqualWith";
import isObject from "lodash/isObject";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";
import { create } from "mutative";
import { z } from "zod";
import {
  assertGetEvaluable,
  assertGetEvaluator,
  assertGetField,
  assertGetFunder,
  assertGetMember,
  assertGetProposal,
  assertGetApplicationFormAttachment,
  assertGetProposalForm,
  assertGetRecommender,
  assertShortnameIsUnique,
  getAllFunders,
  getEvaluable,
  getEvaluation,
  getEvaluator,
  getHoldValue,
  getMemberForEvaluator,
  getProposal,
  getProposalFormOfProposalId,
  getRecusalForEvaluator,
  getRoundMemberForEvaluator,
  isRoundArchived,
  roundUsesTracks,
} from "./round/accessors";
import {
  currentAction,
  type CurrentAction,
  type AddRound,
  removedAction,
  type StampableAction,
  TIMESTAMPED_ACTION_TYPES,
} from "./round/actions";
import {
  getFieldResponsesId,
  type Evaluation,
  type Field,
  type Proposal,
  type Recommender,
  type RoundAggregate,
  type RoundMember,
  type TypedFields,
} from "./round/aggregate";
import { getFunderBudgetFromIncrementalEvalSteps } from "./round/incrementalEvalsUtils";
import type { ProposalInfoFields, SideEffect } from "./round/infoStore";
import { initEvaluator } from "./round/initEvaluator";
import normalizeMvfProfile from "./round/normalizeMvfProfile";
import {
  ActionNotPermittedError,
  ARCHIVED_ROUND_ERROR,
  isActionPermitted,
} from "./round/permissions";
import { resolvePlanForImpact } from "./round/planForImpact";
import {
  ensureMemberInProposalMemberAxes,
  ensureProposalInProposalMemberAxes,
  getOrCreateProposalDisclosure,
  getProposalDisclosure,
  setInvestigation,
  setReviewProgress,
} from "./round/proposalMemberData";
import { recusalDecision, recusalVoters } from "./round/recusals";
import upcastAction, { upcastCurrentAction } from "./round/upcast";

export interface Aggregate {
  type: string;
  version: number;
  id: string;
  latestActionId: string;
}

export const actionMessage = z.object({
  aggregateId: z.string(),
  payload: currentAction,
  // 99% of the time, underlyingUserId and actingAsUserId are the same.
  // They're only different when an admin acts as a user.
  // Nullable for system-dispatched actions that have no human actor.
  underlyingUserId: z.string().nullable(),
  actingAsUserId: z.string().nullable(),
  id: z.string(),
  version: z.number(),
  timestamp: z.string(),

  // undoes is set as metadata for both undos and redos.
  // For a redo it's the undo action that we're undoing.
  undoes: z.string().nullable(),
  // redoes is set as metadata for only redos (not undos).
  // It's the action that the undo that we're undoing undid 🤔
  redoes: z.string().nullable(),
  createdBySfcAdmin: z.boolean().optional(),

  // If set, this action will be skipped during replay (but version/latestActionId still updated)
  ignoredReason: z.string().nullable(),
  // If set, this action was submitted via an agent token.
  agentTokenId: z.string().nullable().optional(),
});

export const unconfirmedActionMessage = actionMessage.omit({
  version: true,
  timestamp: true,
  ignoredReason: true,
});

export type ActionMessage = z.infer<typeof actionMessage>;
export type UnconfirmedActionMessage = z.infer<typeof unconfirmedActionMessage>;

// Messages as they ARRIVE at the reducer and storage adapters: persisted rows
// predating payload timestamps, stale-client submissions, and test fixtures
// may lack payload.timestamp (StampableAction). performAction injects the
// envelope value before application, and the storage adapters inject it via
// ensureActionPayloadTimestamp on every message they return or send to
// clients — so everything downstream (translatedAction, confirmed messages,
// ConfirmActions broadcasts) is a strict ActionMessage. Rows persist exactly
// what was submitted, so a resubmit stays byte-identical.
export type IncomingActionMessage = Omit<ActionMessage, "payload"> & {
  payload: StampableAction;
};
export type IncomingUnconfirmedActionMessage = Omit<
  UnconfirmedActionMessage,
  "payload"
> & {
  payload: StampableAction;
};

// A persisted action predating payload timestamps (or missed by the
// backfill) gets its envelope timestamp copied into the payload on load —
// the exact value application historically used — so client-side parsing
// (which requires the field) and the reducer both see it. No-op for stamped
// payloads and for types that don't carry a timestamp.
export function ensureActionPayloadTimestamp(
  message: ActionMessage,
): ActionMessage {
  const payload = message.payload as { type: string; timestamp?: string };
  if (
    !TIMESTAMPED_ACTION_TYPES.has(payload.type) ||
    payload.timestamp != null
  ) {
    return message;
  }
  return {
    ...message,
    payload: {
      ...message.payload,
      timestamp: message.timestamp,
    } as CurrentAction,
  };
}

export const EPSILON = 0.0000001;

function getDefaultEvaluationMvf(): MVF {
  return [
    { dollar: 0, value: 0.5, placedManually: true },
    { dollar: 100000, value: 0.5, placedManually: true },
    { dollar: 100000, value: 0, placedManually: true },
  ];
}

function ensureRecommenderEvaluationHasMvf(
  evaluator: Evaluator,
  evaluableId: string,
) {
  if (evaluator.type !== "recommender") {
    return;
  }

  if (evaluator.mvfProfile[evaluableId] == null) {
    evaluator.mvfProfile[evaluableId] = getDefaultEvaluationMvf();
    evaluator.mvfChanges = (evaluator.mvfChanges ?? 0) + 1;
  }
}

function applyDeprecatedMatchingAdjustment(
  round: RoundAggregate,
  evaluator: Evaluator,
): void {
  if (
    evaluator.type === "funder" &&
    round.deprecatedMatchingAdjustmentIntensity != null
  ) {
    evaluator.matchingBudgetRatio = round.deprecatedMatchingAdjustmentIntensity;
  }
}

// Treat missing and undefined properties, and floating point numbers with a
// small tolerance as equal
// https://stackoverflow.com/a/57894104/24041125
function comparisonFunc(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean | undefined {
  if (isArray(a) || isArray(b)) return;
  if (!isObject(a) || !isObject(b)) {
    // Handle number comparison with tolerance for floating point precision
    if (typeof a === "number" && typeof b === "number") {
      return Math.abs(a - b) < EPSILON;
    }
    return;
  }

  if (!includes(a, undefined) && !includes(b, undefined)) return;

  return isEqualWith(
    omitBy(a, (value) => value === undefined),
    omitBy(b, (value) => value === undefined),
    comparisonFunc,
  );
}

// Structural equality, but treating missing and undefined properties, and
// floating point numbers with a small tolerance as equal
export function actionIsIdentical(
  action: StampableAction,
  otherAction: StampableAction,
): boolean {
  return isEqualWith(action, otherAction, comparisonFunc);
}

/**
 * Push a WRITE_PROPOSAL_INFO side effect built from the proposal's current
 * state. Pass `fieldsAndResponses` when the proposal's field responses are
 * available so planForImpact is (re)derived; otherwise the stored value is
 * left unchanged. The hold proposal is skipped — it's not a real proposal.
 */
/** Effects outlive the apply: the draft they were built from is a revoked
 * proxy by the time a store resolves them, so anything an intent carries must
 * be a plain copy, never a reference into the draft. Fields are flat. */
function plainFields(fields: Field[]): Field[] {
  return fields.map((field) => ({ ...field }));
}

/** The proposal-info columns the info store writes, taken from the aggregate. */
function proposalInfoFields(
  proposal: Pick<
    Proposal,
    "id" | "shortname" | "longname" | "typedFields" | "status"
  >,
): ProposalInfoFields {
  return {
    proposalId: proposal.id,
    shortname: proposal.shortname,
    longname: proposal.longname,
    receivingOrgName: proposal.typedFields.receivingOrgName || null,
    baseRequestAmount: proposal.typedFields.baseRequestAmount ?? null,
    status: proposal.status,
  };
}

function pushProposalInfoEffect(
  draft: RoundAggregate,
  sideEffects: SideEffect[],
  roundId: string,
  proposal: Pick<
    Proposal,
    "id" | "shortname" | "longname" | "typedFields" | "status"
  >,
  fieldsAndResponses?: {
    fields: Field[];
    fieldResponses: Record<string, string>;
  },
): void {
  if (proposal.id === draft.holdProposalId) return;
  sideEffects.push({
    type: "WRITE_PROPOSAL_INFO",
    roundId,
    proposalId: proposal.id,
    shortname: proposal.shortname,
    longname: proposal.longname,
    receivingOrgName: proposal.typedFields.receivingOrgName || null,
    baseRequestAmount: proposal.typedFields.baseRequestAmount ?? null,
    status: proposal.status,
    ...(fieldsAndResponses
      ? {
          planForImpact: resolvePlanForImpact(
            fieldsAndResponses.fields,
            fieldsAndResponses.fieldResponses,
          ),
        }
      : {}),
  });
}

// The envelope fields applyAction may still read. All are chosen by the
// submitting client and preserved verbatim on confirm — unlike the envelope
// timestamp/version, which the server reassigns — so application stays
// deterministic between the optimistic apply and the confirm re-apply.
type ActionMeta = {
  actionId: string;
  actingAsUserId: string | null;
  /**
   * The action id this message undoes (set for undos and redos). Like the
   * Server's semantic-validation exemption, it is client-supplied and
   * deliberately unverified against the action log — handlers may relax
   * fresh-dispatch availability rules for restores, but must never treat it
   * as a security boundary (permissions always apply).
   */
  undoes: string | null;
};

/**
 * Apply the action, mutating the round aggregate in place.
 *
 * Deliberately NOT given the message envelope: application is a pure function
 * of (round, payload, meta), identical between the optimistic apply and the
 * confirm re-apply. Handlers that persist a time read `action.timestamp` —
 * the creation time carried in the payload (see actionTimestamp in
 * round/actions.ts) — never a clock or the envelope.
 */
function applyAction(
  draft: RoundAggregate,
  action: CurrentAction,
  meta: ActionMeta,
  sideEffects: SideEffect[],
): void {
  switch (action.type) {
    case "CREATE_ROUND": {
      // Trackless rounds keep every member in MAIN; mirror the normalization in
      // initializeRoundAggregate so the persisted side effects don't write an
      // off-MAIN track the hidden picker can never correct.
      const memberTrack = roundUsesTracks(draft) ? action.track : "MAIN";
      sideEffects.push({
        type: "CREATE_ROUND",
        roundId: draft.id,
        name: action.name,
      });
      sideEffects.push({
        type: "ADD_ROUND_MEMBER",
        roundId: draft.id,
        userId: action.userId,
        track: memberTrack,
        deanonymize: action.deanonymize ?? false,
      });
      sideEffects.push({
        type: "ADD_ROUND_MEMBER",
        roundId: draft.id,
        userId: meanUserId,
        track: memberTrack,
        deanonymize: true,
      });
      return;
    }
    case "ADD_ROUND_MEMBER": {
      const { userId, initials, track } = action;
      // Trackless rounds keep every member in MAIN (the picker is hidden), so
      // ignore any non-MAIN track a non-default client might send.
      const memberTrack = roundUsesTracks(draft) ? track : "MAIN";
      const existingMember = draft.members[userId];
      let deanonymize: boolean;

      if (existingMember) {
        // If this user is already present in the round, either restore them
        // (if they were removed from the round), or throw an error (if they
        // were not previously removed and are being double-added).
        if (existingMember.status !== "removed") {
          throw new Error(
            `User with id ${userId} already exists: ${existingMember.initials}`,
          );
        }
        deanonymize = action.deanonymize ?? existingMember.deanonymize;
        draft.members[userId] = {
          ...existingMember,
          status: "active",
          deanonymize,
        };
      } else {
        deanonymize = action.deanonymize ?? false;
        draft.members[userId] = {
          initials,
          createdAt: action.timestamp,
          status: "active" as const,
          userId,
          id: userId,
          isRoundAdmin: false,
          track: memberTrack,
          feedbackVisibleByDefault: true,
          evaluableFeedback: {},
          evaluableNotes: {},
          evaluablePermissions: {},
          evaluators: {},
          deanonymize,
        };
      }

      ensureMemberInProposalMemberAxes(draft, userId);

      sideEffects.push({
        type: "ADD_ROUND_MEMBER",
        roundId: draft.id,
        userId,
        track: memberTrack,
        deanonymize,
      });
      break;
    }
    case "ADD_PROPOSAL_FORM": {
      const {
        name,
        proposalFormId,
        googleFormId,
        spreadsheetId,
        spreadsheetRange,
        roundIdentifier,
      } = action;

      if (draft.proposalForms[proposalFormId]) {
        console.warn(
          `ADD_PROPOSAL_FORM: Proposal form with id ${proposalFormId} already exists`,
        );
        break;
      }

      draft.proposalForms[proposalFormId] = {
        id: proposalFormId,
        status: "active",
        fields: [],
        name,
        proposals: {},
        googleFormId,
        spreadsheetId,
        spreadsheetRange,
        roundIdentifier,
      };
      break;
    }
    case "ADD_OR_UPDATE_PROPOSAL_FORM_FIELD": {
      const { field, proposalFormId } = action;

      const proposalForm = assertGetProposalForm(draft, proposalFormId);

      const fieldIndex = proposalForm.fields.findIndex(
        (f) => f.id === field.id,
      );

      if (fieldIndex !== -1) {
        proposalForm.fields[fieldIndex] = field;
      } else {
        proposalForm.fields.push(field);
      }
      break;
    }
    case "ADD_EVALUATOR": {
      const {
        memberId: userId,
        shortname,
        evaluatorType,
        evaluatorId,
        threshold,
      } = action;

      assertShortnameIsUnique(draft, shortname);
      const evaluator = initEvaluator(
        evaluatorId,
        userId,
        shortname,
        evaluatorType,
        draft.holdProposalId,
      );
      applyDeprecatedMatchingAdjustment(draft, evaluator);

      if (
        evaluator.type === "recommender" &&
        evaluator.memberId === meanUserId
      ) {
        evaluator.meanRecommenderThreshold = threshold;
      } else if (threshold !== undefined) {
        throw new Error("Threshold specified for non-mean recommender");
      }

      const member = assertGetMember(draft, userId);
      const existingEvaluator = getEvaluator(draft, evaluatorId);
      if (existingEvaluator) {
        throw new Error(`Evaluator with id ${evaluatorId} already exists`);
      }

      member.evaluators[evaluatorId] = evaluator;
      break;
    }
    case "ADD_PROPOSAL": {
      const {
        proposalId,
        entityType,
        proposalLongname,
        proposalFirstSubmittedDatetime,
        proposalShortname,
        proposalExternalId,
        proposalFormId,
        matchRate,
        maxStandardAmount,
        baseRequestAmount,
        targetsFreedom,
        targetsFairness,
        longformLink,
        keyIndividuals,
        fieldResponses = {},
      } = action;

      const proposalForm = assertGetProposalForm(draft, proposalFormId);

      if (proposalId in proposalForm.proposals) {
        console.warn(
          `ADD_PROPOSAL: Proposal with id ${proposalId} already exists`,
        );
        break;
        // throw new Error(`Proposal with id ${proposalId} already exists`);
      }

      const resolvedMaxStandardAmount =
        maxStandardAmount != null ? maxStandardAmount : undefined;

      const typedFields: TypedFields = {
        matchRate,
        maxStandardAmount: resolvedMaxStandardAmount,
        baseRequestAmount: baseRequestAmount ?? 0,
        receivingOrgName: "",
        receivingOrgTaxId: "",
        receivingOrgLocation: "",
        sffApplicationId: "",
        targetsFreedom: targetsFreedom ?? false,
        targetsFairness: targetsFairness ?? false,
        longformLink: longformLink ?? "",
        keyIndividuals: keyIndividuals ?? [],
      };

      proposalForm.proposals[proposalId] = {
        id: proposalId,
        type: "proposal",
        entityType,
        shortname: proposalShortname,
        externalId: proposalExternalId,
        longname: proposalLongname ?? proposalShortname,
        firstSubmittedDatetime: proposalFirstSubmittedDatetime,
        lastSubmittedDatetime: proposalFirstSubmittedDatetime,
        status: "active",
        requestsMatchingPledge: false,
        typedFields,
        fieldResponsesId: getFieldResponsesId(fieldResponses),
      };

      ensureProposalInProposalMemberAxes(draft, proposalId);

      sideEffects.push({
        type: "WRITE_FIELD_RESPONSES",
        roundId: draft.id,
        proposalId,
        fieldResponses,
      });
      pushProposalInfoEffect(
        draft,
        sideEffects,
        draft.id,
        proposalForm.proposals[proposalId],
        { fields: proposalForm.fields, fieldResponses },
      );

      break;
    }
    case "START_EVALUATION": {
      const { evaluableId, evaluatorId, mvf } = action;

      const evaluable = assertGetEvaluable(draft, evaluableId);
      if (evaluable.status === "removed" || evaluable.status === "withdrawn") {
        throw new Error("Evaluable is unavailable");
      }

      const recusal = getRecusalForEvaluator(draft, evaluatorId, evaluableId);
      if (recusal) {
        throw new Error("Evaluator is recused");
      }

      const evaluator = assertGetEvaluator(draft, evaluatorId);
      const roundMember = getRoundMemberForEvaluator(draft, evaluatorId);

      if (!roundMember) {
        throw new Error("Round member not found");
      }

      if (evaluator.type === "funder") {
        break;
      }

      const existingEvaluation = getEvaluation(draft, evaluatorId, evaluableId);
      if (existingEvaluation) {
        if (
          existingEvaluation.state === "deferred" ||
          existingEvaluation.state === "recused"
        ) {
          throw new Error(
            "Evaluations can't be moved to active from deferred or recused",
          );
        } else if (existingEvaluation.state === "evaluated") {
          console.warn(
            `Evaluation is already active (evaluator: ${evaluatorId}, evaluable: ${evaluableId})`,
          );
        }
      }

      if (evaluableId === evaluatorId) {
        throw new Error("Evaluator cannot evaluate themselves");
      }

      const evaluation: Evaluation = {
        evaluableId: evaluableId,
        evaluableType: evaluable.type,
        state: "evaluated",
        setToZero: false,
        hidden: false,
        lastTouchedAt: action.timestamp,
      };

      let newMvf: MVF = mvf ?? getDefaultEvaluationMvf();

      const validationError = validatePoints(newMvf, false);
      if (validationError.type === "error") {
        newMvf = getDefaultEvaluationMvf();
      }

      evaluator.evaluations[evaluableId] = evaluation;

      // ANNOTATIONS SURVIVING THE REBUILD. Unlike every other evaluation
      // case, this one REPLACES the record rather than mutating it, so any
      // field on `Evaluation` that outlives a restart has to be carried
      // across by hand here — the object literal above is complete by
      // construction, so an omission is silent and the type system won't
      // catch it. Anything added to the record should be considered here.
      if (existingEvaluation?.negativeValue === true) {
        // Carried UNCONDITIONALLY, unlike the verdict below. The claim is
        // independent of the curve — an MVF bottoms out at zero, so no curve
        // can contradict "I think this does damage" — which means the curve
        // this action installs has no bearing on it.
        evaluation.negativeValue = true;
      }

      if (evaluator.type === "recommender") {
        evaluator.mvfProfile[evaluableId] = newMvf;
        evaluator.mvfChanges = (evaluator.mvfChanges ?? 0) + 1;
        // Same retraction rule as curve edits: starting with a curve that is
        // still zero/at-or-below hold does not contradict a standing verdict,
        // so carry it across the rebuild instead of silently dropping it.
        if (
          existingEvaluation?.finality != null &&
          !mvfIsMeaningfullyValued(
            newMvf,
            getHoldValue(evaluator.mvfProfile, draft.holdProposalId),
          )
        ) {
          evaluation.finality = existingEvaluation.finality;
        }
      }
      break;
    }
    case "REMOVE_ROUND":
      draft.status = "deleted";
      sideEffects.push({
        type: "REMOVE_ROUND",
        roundId: draft.id,
      });
      for (const member of Object.values(draft.members)) {
        sideEffects.push({
          type: "REMOVE_ROUND_MEMBER",
          roundId: draft.id,
          userId: member.userId,
        });
      }
      break;
    case "REMOVE_ROUND_MEMBER": {
      const { actingAsUserId: userId } = meta;
      const { userId: id } = action;

      if (userId === id) {
        throw new Error("You cannot remove yourself");
      }

      // This action was created before this rule existed.
      const isOneTimeAdmittedAction = meta.actionId === "a-SunEfMoZEzOw";
      if (getOtherFunders(draft, id).length === 0 && !isOneTimeAdmittedAction) {
        throw new Error("At least one funder is required");
      }

      const member = assertGetMember(draft, id);
      member.status = "removed";
      sideEffects.push({
        type: "REMOVE_ROUND_MEMBER",
        roundId: draft.id,
        userId: id,
      });
      break;
    }
    case "REMOVE_PROPOSAL_FORM": {
      const { proposalFormId } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      proposalForm.status = "deleted";
      break;
    }
    case "PROPOSAL_FORM_ROUND_IDENTIFIER_CHANGE": {
      const { proposalFormId, roundIdentifier } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      proposalForm.roundIdentifier = roundIdentifier;
      break;
    }
    case "ATTACH_APPLICATION_FORM": {
      const { proposalFormId, formId, name } = action;
      if (draft.proposalForms[proposalFormId]) {
        console.warn(
          `ATTACH_APPLICATION_FORM: Proposal form with id ${proposalFormId} already exists`,
        );
        break;
      }
      // One attachment per form per round: a second would create every
      // application twice, and the Proposal Forms page offers only unattached
      // forms, so a duplicate can only come from a stale client.
      const alreadyAttached = Object.values(draft.proposalForms).find(
        (form) =>
          form.status === "active" && form.applicationForm?.formId === formId,
      );
      if (alreadyAttached) {
        throw new Error(
          `ATTACH_APPLICATION_FORM: form ${formId} is already attached as "${alreadyAttached.name}"`,
        );
      }
      draft.proposalForms[proposalFormId] = {
        id: proposalFormId,
        status: "active",
        fields: [],
        name,
        proposals: {},
        // No spreadsheet: the same empty ids the round's default form uses,
        // which keeps it out of the spreadsheet sync (see
        // isSpreadsheetBackedProposalForm).
        googleFormId: "",
        spreadsheetId: "",
        spreadsheetRange: "",
        applicationForm: { formId, fieldMapping: {}, optionValues: {} },
      };
      break;
    }
    case "DETACH_APPLICATION_FORM": {
      const { proposalFormId } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      if (!proposalForm.applicationForm) {
        console.warn(
          `DETACH_APPLICATION_FORM: Proposal form ${proposalFormId} has no attachment`,
        );
        break;
      }
      // Not REMOVE_PROPOSAL_FORM: that marks the form deleted, and every
      // proposal list starts from active forms, so the proposals created
      // from this source would vanish with it. Detaching only stops new
      // applications arriving.
      delete proposalForm.applicationForm;
      sideEffects.push({
        type: "RETIRE_APPLICATION_FORM_MAPPING",
        roundId: draft.id,
        proposalFormId,
        actionId: meta.actionId,
      });
      break;
    }
    case "APPLICATION_FORM_FIELD_MAPPING_CHANGE": {
      const { proposalFormId, fieldId, questionId } = action;
      const attachment = assertGetApplicationFormAttachment(
        draft,
        proposalFormId,
      );
      const previous = attachment.fieldMapping[fieldId];
      if (questionId === null) {
        delete attachment.fieldMapping[fieldId];
      } else {
        attachment.fieldMapping[fieldId] = questionId;
      }
      // Option values describe this field's question; pointing the field
      // elsewhere makes them meaningless, so they go with the old question.
      if (previous !== questionId) {
        delete attachment.optionValues[fieldId];
      }
      break;
    }
    case "APPLICATION_FORM_OPTION_VALUE_CHANGE": {
      const { proposalFormId, fieldId, optionId, value } = action;
      const attachment = assertGetApplicationFormAttachment(
        draft,
        proposalFormId,
      );
      if (attachment.fieldMapping[fieldId] === undefined) {
        throw new Error(
          `APPLICATION_FORM_OPTION_VALUE_CHANGE: field ${fieldId} has no question on proposal form ${proposalFormId}`,
        );
      }
      const values = attachment.optionValues[fieldId] ?? {};
      if (value === null) {
        delete values[optionId];
      } else {
        values[optionId] = value;
      }
      if (Object.keys(values).length === 0) {
        delete attachment.optionValues[fieldId];
      } else {
        attachment.optionValues[fieldId] = values;
      }
      break;
    }
    case "REMOVE_PROPOSAL_FORM_FIELD": {
      const { fieldId, proposalFormId } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      const fieldIndex = proposalForm.fields.findIndex((f) => f.id === fieldId);
      if (fieldIndex === -1) {
        const availableFields = proposalForm.fields.map((f) => f.id);
        throw new Error(
          `Field with id ${fieldId} not found (available fields: ${availableFields.join(", ")})`,
        );
      }
      const field = proposalForm.fields[fieldIndex];
      if (!field) {
        throw new Error(`Field with id ${fieldId} not found`);
      }
      field.status = "deleted";
      break;
    }
    case "REMOVE_EVALUATOR": {
      const { evaluatorId } = action;
      const evaluator = assertGetEvaluator(draft, evaluatorId);
      evaluator.status = "removed";
      break;
    }
    case "REMOVE_PROPOSAL": {
      const { proposalId, proposalFormId } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      const proposal = assertGetProposal(proposalForm, proposalId);
      proposal.status = "withdrawn";
      pushProposalInfoEffect(draft, sideEffects, draft.id, proposal);
      break;
    }
    case "READD_PROPOSAL": {
      // Reactivate a previously-withdrawn proposal in place, so its recommender
      // notes and MVFs (keyed by proposal id) come back with it. Re-adding via
      // sync routes through this rather than minting a fresh proposal.
      const { proposalId, proposalFormId } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      const proposal = assertGetProposal(proposalForm, proposalId);
      proposal.status = "active";
      ensureProposalInProposalMemberAxes(draft, proposalId);
      pushProposalInfoEffect(draft, sideEffects, draft.id, proposal);
      break;
    }
    case "ROUND_NAME_CHANGE":
      draft.name = action.newName;
      sideEffects.push({
        type: "UPDATE_ROUND",
        roundId: draft.id,
        update: { name: action.newName },
      });
      break;
    case "ROUND_ARCHIVED_CHANGE": {
      // Idempotent: a retried or stale archive must not move the original
      // stamp, and a restore of a live round must not write anything.
      if (action.archived === isRoundArchived(draft)) {
        break;
      }
      // The aggregate carries the ISO string (it is JSON-serialized into
      // snapshots and the offline cache); the round row gets a real
      // timestamp so list queries can filter and index on it.
      if (action.archived) {
        draft.archivedAt = action.timestamp;
      } else {
        delete draft.archivedAt;
      }
      sideEffects.push({
        type: "UPDATE_ROUND",
        roundId: draft.id,
        update: {
          archivedAt: action.archived ? new Date(action.timestamp) : null,
        },
      });
      break;
    }
    case "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY": {
      const { memberId } = action;
      const member = assertGetMember(draft, memberId);
      if (action.track != null) {
        // Trackless rounds keep every member in MAIN and hide the track picker,
        // so clamp any track change (e.g. from a non-default client) to MAIN.
        const newTrack = roundUsesTracks(draft) ? action.track : "MAIN";
        member.track = newTrack;
        sideEffects.push({
          type: "UPDATE_ROUND_MEMBER",
          roundId: draft.id,
          userId: memberId,
          update: { track: newTrack },
        });
      }
      if (action.initials != null) member.initials = action.initials;
      if (action.admin != null) member.isRoundAdmin = action.admin;
      if (action.isRestrictedFunder != null) {
        member.isRestrictedFunder = action.isRestrictedFunder;
      }
      if (action.hiddenRecommenderMemberIds != null) {
        member.hiddenRecommenderMemberIds = action.hiddenRecommenderMemberIds;
      }
      break;
    }
    case "ROUND_MEMBER_PROPERTY_CHANGE": {
      const { memberId } = action;
      const member = assertGetMember(draft, memberId);
      if (action.feedbackVisibleByDefault != null) {
        member.feedbackVisibleByDefault = action.feedbackVisibleByDefault;
        sideEffects.push({
          type: "UPDATE_ROUND_MEMBER",
          roundId: draft.id,
          userId: memberId,
          update: { feedbackVisibleByDefault: action.feedbackVisibleByDefault },
        });
      }
      if (action.deanonymize != null) {
        member.deanonymize = action.deanonymize;
        sideEffects.push({
          type: "UPDATE_ROUND_MEMBER",
          roundId: draft.id,
          userId: memberId,
          update: { deanonymize: action.deanonymize },
        });
      }
      break;
    }
    case "AUTHORIZE_AGENT_TOKEN_FOR_ROUND": {
      sideEffects.push({
        type: "AUTHORIZE_AGENT_TOKEN_FOR_ROUND",
        roundId: draft.id,
        tokenId: action.tokenId,
      });
      break;
    }
    case "DEAUTHORIZE_AGENT_TOKEN_FOR_ROUND": {
      sideEffects.push({
        type: "DEAUTHORIZE_AGENT_TOKEN_FOR_ROUND",
        roundId: draft.id,
        tokenId: action.tokenId,
      });
      break;
    }
    case "EVALUATOR_PROPERTY_CHANGE": {
      const { evaluatorId, property, value } = action;
      const evaluator = assertGetEvaluator(draft, evaluatorId);
      if (property === "shortname") {
        evaluator.shortname = value;
      }
      break;
    }
    case "FUNDER_BUDGET_CHANGE": {
      const { funderId, budget } = action;
      const funder = assertGetFunder(draft, funderId);
      funder.budget = budget;
      break;
    }
    case "FUNDER_SETTLEMENT_BUDGET_CHANGE": {
      const { funderId, budget } = action;
      const funder = assertGetFunder(draft, funderId);

      funder.speculationGrantSettlementBudget = budget;
      break;
    }
    case "FUNDER_RATINGS_IMPORT": {
      const { funderId, scores } = action;
      const funder = assertGetFunder(draft, funderId);

      for (const scoreRow of scores) {
        const { recommenderId, proposalId, rating: score } = scoreRow;

        if (!funder.scores[recommenderId]) {
          funder.scores[recommenderId] = {};
        }

        funder.scores[recommenderId][proposalId] = score;
      }
      break;
    }
    case "FUNDER_RATING_CHANGE": {
      const { funderId, recommenderId, proposalId, rating: score } = action;
      const funder = assertGetFunder(draft, funderId);
      if (score == null) {
        if (funder.scores[recommenderId]) {
          delete funder.scores[recommenderId][proposalId];
        }
      } else {
        if (!funder.scores[recommenderId]) {
          funder.scores[recommenderId] = {};
        }
        funder.scores[recommenderId][proposalId] = score;
      }
      break;
    }
    // Legacy round-wide clear: no longer dispatched, but historical event logs
    // replay it, so it keeps its original wipe-every-funder behavior.
    case "FUNDER_RATINGS_CLEAR": {
      for (const funder of getAllFunders(draft)) {
        funder.scores = {};
      }
      break;
    }
    case "CLEAR_FUNDER_RATINGS": {
      assertGetFunder(draft, action.funderId).scores = {};
      break;
    }
    case "FUNDER_PROPOSAL_WEIGHT_CHANGE": {
      const { funderId, proposalId, weight } = action;
      const funder = assertGetFunder(draft, funderId);
      if (weight == null) {
        delete funder.proposalWeights[proposalId];
      } else {
        funder.proposalWeights[proposalId] = weight;
      }
      break;
    }
    case "FUNDER_PROPOSAL_EXCLUSION_TOGGLE": {
      const { funderId, proposalId, excluded } = action;
      const funder = assertGetFunder(draft, funderId);
      const already = funder.excludedProposalIds.includes(proposalId);
      if (excluded && !already) {
        funder.excludedProposalIds.push(proposalId);
      } else if (!excluded && already) {
        funder.excludedProposalIds = funder.excludedProposalIds.filter(
          (id) => id !== proposalId,
        );
      }
      break;
    }
    case "FUNDER_RECOMMENDER_WEIGHT_CHANGE": {
      const { funderId, recommenderId, weight } = action;
      const funder = assertGetFunder(draft, funderId);
      if (weight == null) {
        delete funder.recommenderWeights[recommenderId];
      } else {
        funder.recommenderWeights[recommenderId] = weight;
      }
      break;
    }
    case "INCREMENTAL_EVALS_PUSH": {
      const { evaluatorId, evaluableId, incrementAmount } = action;

      const funder = assertGetFunder(draft, evaluatorId);

      // Taking a new step clears the redoable steps
      if (funder.undoneIncrementalEvalSteps.length > 0) {
        funder.undoneIncrementalEvalSteps = [];
      }

      funder.incrementalEvalSteps.push({
        evaluableId,
        incrementAmount,
      });

      funder.budget = getFunderBudgetFromIncrementalEvalSteps(
        funder.incrementalEvalSteps,
        draft.holdProposalId,
      );
      break;
    }
    case "INCREMENTAL_EVALS_UNDO": {
      const { evaluatorId } = action;

      const funder = assertGetFunder(draft, evaluatorId);
      const undoneStep = funder.incrementalEvalSteps.pop();
      if (!undoneStep) {
        throw new Error("Nothing left to undo");
      }

      funder.undoneIncrementalEvalSteps.unshift(undoneStep);

      funder.budget = getFunderBudgetFromIncrementalEvalSteps(
        funder.incrementalEvalSteps,
        draft.holdProposalId,
      );
      break;
    }
    case "INCREMENTAL_EVALS_REDO": {
      const { evaluatorId } = action;

      const funder = assertGetFunder(draft, evaluatorId);
      const redoStep = funder.undoneIncrementalEvalSteps.shift();
      if (!redoStep) {
        throw new Error("Nothing left to redo");
      }

      funder.incrementalEvalSteps.push(redoStep);

      funder.budget = getFunderBudgetFromIncrementalEvalSteps(
        funder.incrementalEvalSteps,
        draft.holdProposalId,
      );
      break;
    }
    case "EVALUATION_FEEDBACK_CHANGE": {
      const { memberId, evaluableId, positive, negative } = action;
      const member = draft.members[memberId];
      if (!member) {
        throw new Error("Member not found");
      }
      if (!(evaluableId in member.evaluableFeedback)) {
        member.evaluableFeedback[evaluableId] = {
          positiveNegative: {
            positive: "",
            negative: "",
          },
        };
      }
      const feedback = member.evaluableFeedback[evaluableId];
      if (!feedback) {
        throw new Error("Feedback entry not found");
      }

      if (positive != null) feedback.positiveNegative.positive = positive;
      if (negative != null) feedback.positiveNegative.negative = negative;
      touchRecommenderEvaluation(draft, member, evaluableId, action.timestamp);
      break;
    }
    case "EVALUATION_NOTES_CHANGE": {
      const { memberId, evaluableId, notes } = action;
      const member = draft.members[memberId];
      if (!member) {
        console.warn(
          `User ${memberId} not found, valid user ids:`,
          Object.keys(draft.members),
        );
        throw new Error("User not found");
      }
      member.evaluableNotes[evaluableId] = notes;
      touchRecommenderEvaluation(draft, member, evaluableId, action.timestamp);
      sideEffects.push({
        type: "WRITE_MEMBER_NOTE",
        roundId: draft.id,
        memberId,
        proposalId: evaluableId,
        notes,
        lastTouchedAt: action.timestamp,
      });
      break;
    }
    case "GENERAL_NOTES_CHANGE": {
      const { memberId, notes } = action;
      const member = draft.members[memberId];
      if (!member) {
        console.warn(
          `User ${memberId} not found, valid user ids:`,
          Object.keys(draft.members),
        );
        throw new Error("User not found");
      }
      member.generalNotes = notes;
      break;
    }
    case "NOTES_AND_FEEDBACK_VISIBILITY_CHANGE": {
      const { memberId, evaluableId, visibility } = action;
      const member = draft.members[memberId];
      if (!member) {
        throw new Error("Member not found");
      }
      member.evaluablePermissions[evaluableId] = visibility;
      sideEffects.push({
        type: "WRITE_NOTE_VISIBILITY",
        roundId: draft.id,
        memberId,
        proposalId: evaluableId,
        visibility,
      });
      break;
    }
    case "EVALUATION_MVF_CHANGE": {
      const { evaluatorId, evaluableId, setZero } = action;
      const evaluator = getEvaluator(draft, evaluatorId);
      if (!evaluator) {
        console.warn(
          `EVALUATION_MVF_CHANGE: Evaluator not found (evaluator: ${evaluatorId}, evaluable: ${evaluableId})`,
        );
        return;
      }
      if (evaluator.type !== "recommender") {
        console.warn(
          `EVALUATION_MVF_CHANGE: Evaluator is not a recommender (evaluator: ${evaluatorId}, evaluable: ${evaluableId})`,
        );
        return;
      }

      // Action a-k-0ZdvsSF6DK was created when we used the wrong validation
      if (meta.actionId !== "a-k-0ZdvsSF6DK") {
        const validationResult = validatePoints(
          action.mvf,
          evaluableId === draft.holdProposalId,
        );
        if (validationResult.type === "error") {
          throw new Error("Invalid MVF: " + validationResult.message);
        }
      }

      evaluator.mvfProfile[evaluableId] = action.mvf;

      const evaluation = evaluator.evaluations[evaluableId];
      if (evaluation) {
        evaluation.setToZero = Boolean(setZero);
        // Drawing a curve that actually values the evaluable retracts any
        // standing finality verdict — a real curve supersedes "worthless /
        // never looked". A zero or fully at/below-hold curve is consistent
        // with a verdict, so it stays. (Clearing never throws, so replay of
        // historical actions can't be invalidated by this.)
        if (
          mvfIsMeaningfullyValued(
            action.mvf,
            getHoldValue(evaluator.mvfProfile, draft.holdProposalId),
          )
        ) {
          evaluation.finality = undefined;
        }
        evaluation.lastTouchedAt = action.timestamp;
      }
      break;
    }
    case "MATCHING_MVF_CHANGE": {
      const { evaluatorId, evaluableId, matchingMvfScales } = action;
      const evaluator = getEvaluator(draft, evaluatorId);
      if (!evaluator) {
        console.warn(
          `MATCHING_MVF_CHANGE: Evaluator not found (evaluator: ${evaluatorId}, evaluable: ${evaluableId})`,
        );
        return;
      }
      if (evaluator.type !== "recommender") {
        console.warn(
          `MATCHING_MVF_CHANGE: Evaluator is not a recommender (evaluator: ${evaluatorId}, evaluable: ${evaluableId})`,
        );
        return;
      }

      evaluator.matchingMvfScales[evaluableId] = matchingMvfScales;

      const evaluation = evaluator.evaluations[evaluableId];
      if (evaluation) {
        evaluation.lastTouchedAt = action.timestamp;
      }
      break;
    }
    case "EVALUATION_SET_ZERO": {
      const { evaluatorId, evaluableId } = action;
      const recommender = assertGetRecommender(draft, evaluatorId);
      const evaluable = assertGetEvaluable(draft, evaluableId);

      if (evaluable.status === "removed" || evaluable.status === "withdrawn") {
        throw new Error("Evaluable is unavailable");
      }

      recommender.mvfProfile[evaluableId] = [
        { dollar: 0, value: 0, placedManually: true },
        { dollar: 100000, value: 0, placedManually: true },
      ];
      if (recommender.type === "recommender") {
        recommender.mvfChanges = (recommender.mvfChanges ?? 0) + 1;
      }

      const evaluation = recommender.evaluations[evaluableId];
      if (evaluation) {
        if (["deferred", "recused"].includes(evaluation.state)) {
          throw new Error(
            "Evaluation is deferred or recused, so can't be set to 0.",
          );
        }
        evaluation.state = "evaluated";
        evaluation.setToZero = true;
        evaluation.lastTouchedAt = action.timestamp;
      } else {
        recommender.evaluations[evaluableId] = {
          state: "evaluated",
          evaluableId,
          evaluableType: evaluable.type,
          setToZero: true,
          hidden: false,
          lastTouchedAt: action.timestamp,
        };
      }
      break;
    }
    case "EVALUATION_SET_HIDDEN": {
      const { evaluatorId, evaluableId, hidden } = action;
      const evaluator = assertGetRecommender(draft, evaluatorId);
      const evaluable = assertGetEvaluable(draft, evaluableId);

      if (evaluable.status === "removed" || evaluable.status === "withdrawn") {
        throw new Error("Evaluable is unavailable");
      }

      let evaluation = evaluator.evaluations[evaluableId];
      if (!evaluation) {
        evaluation = {
          state: "not-started",
          evaluableId,
          evaluableType: evaluable.type,
          setToZero: false,
          hidden,
          lastTouchedAt: action.timestamp,
        };
        evaluator.evaluations[evaluableId] = evaluation;
      } else {
        if (evaluation.state === "recused" && !hidden) {
          throw new Error("Recused evaluations cannot be unhidden");
        }
        evaluation.hidden = hidden;
        evaluation.lastTouchedAt = action.timestamp;
      }
      break;
    }
    case "EVALUATION_FINALITY_CHANGE": {
      const { evaluatorId, evaluableId, finality } = action;
      const evaluator = assertGetRecommender(draft, evaluatorId);
      const evaluable = assertGetEvaluable(draft, evaluableId);

      // Retracting a verdict is unconditional: whatever has happened to the
      // evaluable, the hold line, or the curve since, the recommender can
      // always take the statement back.
      if (finality == null) {
        const evaluation = evaluator.evaluations[evaluableId];
        if (evaluation?.finality != null) {
          evaluation.finality = undefined;
          evaluation.lastTouchedAt = action.timestamp;
        }
        break;
      }

      let evaluation = evaluator.evaluations[evaluableId];

      // The availability gates below apply to fresh declarations only.
      // Undo/redo restores (meta.undoes set, mirroring the Server's
      // semantic-validation exemption) skip them: clears are unconditional,
      // so their inverses must be dispatchable even after a recusal,
      // withdrawal, or hold-line move. `undoes` is persisted on the action
      // row and replayed verbatim, so replay is deterministic.
      //
      // `undoes` is client-supplied and deliberately unverified (see the
      // rationale in Server.ts: an existence check races legitimate undos of
      // still-unconfirmed actions). That is acceptable here because these
      // gates are a UX-coherence rule, NOT an invariant: verdict-beside-
      // meaningful-curve is already reachable through fully legitimate
      // dispatches (raise hold → declare → lower hold; the verdict is
      // deliberately kept). A crafted `undoes` lets a recommender mislabel
      // only their own evaluation, visibly, retractably, and the label
      // self-clears on the next meaningful curve edit — validating the
      // reference would buy no invariant while re-introducing the stranded-
      // undo race. Permissions and evals-freeze always apply regardless.
      if (meta.undoes == null) {
        if (
          evaluable.status === "removed" ||
          evaluable.status === "withdrawn"
        ) {
          throw new Error("Evaluable is unavailable");
        }

        if (evaluation && ["deferred", "recused"].includes(evaluation.state)) {
          throw new Error(
            "Evaluation is deferred or recused, so a finality verdict can't be recorded.",
          );
        }

        // The availability gate, enforced here (not just in the UI) because
        // actions also arrive from stale tabs and direct WebSocket clients.
        // Evaluated against round state as of this action, so replay is
        // deterministic: a later hold-line move can't retroactively fail it.
        // A verdict that a later hold-line move contradicts is deliberately
        // kept until the recommender clears it — see canDeclareFinality.
        if (
          mvfIsMeaningfullyValued(
            evaluator.mvfProfile[evaluableId],
            getHoldValue(evaluator.mvfProfile, draft.holdProposalId),
          )
        ) {
          throw new Error(
            "A finality verdict can only be recorded while the curve is absent, zero, or at/below the hold line",
          );
        }
      }

      if (!evaluation) {
        evaluation = {
          state: "not-started",
          evaluableId,
          evaluableType: evaluable.type,
          setToZero: false,
          hidden: false,
        };
        evaluator.evaluations[evaluableId] = evaluation;
      }
      evaluation.finality = finality;
      evaluation.lastTouchedAt = action.timestamp;
      break;
    }
    case "EVALUATION_NEGATIVE_VALUE_CHANGE": {
      const { evaluatorId, evaluableId, negativeValue } = action;
      const evaluator = assertGetRecommender(draft, evaluatorId);
      const evaluable = assertGetEvaluable(draft, evaluableId);

      // Nothing about the CURVE or the hold line is checked here, on purpose:
      // an MVF bottoms out at zero, so no curve can contradict "I think this
      // does damage" and there is nothing for the reducer to defend.
      //
      // What is checked is whether the evaluation is the claimant's to speak
      // on. These gates live in the reducer rather than `isActionPermitted`
      // for one reason: only the reducer sees `meta.undoes`. Retraction is
      // unconditional (below), so its inverse must be dispatchable too —
      // otherwise a recommender who un-ticks on a recused evaluation cannot
      // undo, which is the state a review pass found. Same exemption, and
      // the same rationale, as EVALUATION_FINALITY_CHANGE above: `undoes` is
      // persisted on the action row and replayed verbatim, so replay stays
      // deterministic.
      //
      // Safe to add as a reducer throw only because no action of this type
      // has ever been recorded — see "Every confirmed action replays forever"
      // in AGENTS.md. Judged against round state as of this action, so a
      // later recusal can't retroactively fail a claim that was legitimate
      // when it was made.
      //
      // `undoes` is client-supplied and deliberately unverified (the
      // rationale is in Server.ts: an existence check races legitimate undos
      // of still-unconfirmed actions). That is acceptable because these two
      // gates are COHERENCE RULES, not invariants — both states they refuse
      // are reachable without any crafted envelope, by claiming while the
      // proposal is active and recusing or withdrawing afterwards, since the
      // claim is deliberately kept (a statement the recommender made, not a
      // derived fact; pinned in negativeValueFlag.test.ts). So a forged
      // `undoes` buys an attacker nothing they could not reach legitimately:
      // it labels only their OWN evaluation, visibly and retractably, while
      // ownership permissions and the evals freeze still apply. Validating
      // the reference would secure no invariant and would re-introduce the
      // stranded-undo race.
      if (negativeValue && meta.undoes == null) {
        if (
          evaluable.status === "removed" ||
          evaluable.status === "withdrawn"
        ) {
          throw new Error(
            "Evaluable is unavailable, so a negative-value claim can't be recorded",
          );
        }
        const existingState = evaluator.evaluations[evaluableId]?.state;
        if (existingState === "deferred" || existingState === "recused") {
          throw new Error(
            "Evaluation is deferred or recused, so a negative-value claim can't be recorded",
          );
        }
      }

      let evaluation = evaluator.evaluations[evaluableId];
      if (!evaluation) {
        evaluation = {
          state: "not-started",
          evaluableId,
          evaluableType: evaluable.type,
          setToZero: false,
          hidden: false,
        };
        evaluator.evaluations[evaluableId] = evaluation;
      }
      // Store the claim, not its absence: `false` becomes undefined so that
      // "never said anything" and "unticked it" are the same state. There is
      // no such statement as "I claim this is NOT harmful", and a stored
      // `false` would invite readers to invent one.
      evaluation.negativeValue = negativeValue ? true : undefined;
      evaluation.lastTouchedAt = action.timestamp;
      break;
    }
    case "EVALUATION_STATE_CHANGE": {
      const { evaluatorId, evaluableId } = action;
      const evaluator = assertGetRecommender(draft, evaluatorId);
      const evaluable = assertGetEvaluable(draft, evaluableId);
      const member = getMemberForEvaluator(draft, evaluatorId);

      if (evaluable.status === "removed" || evaluable.status === "withdrawn") {
        throw new Error("Evaluable is unavailable");
      }

      if (!member) {
        throw new Error(`Member not found for evaluator ${evaluatorId}`);
      }

      let evaluation = evaluator.evaluations[evaluableId];
      if (!evaluation) {
        evaluation = {
          state: "not-started",
          evaluableId,
          evaluableType: evaluable.type,
          setToZero: false,
          hidden: false,
        };
        evaluator.evaluations[evaluableId] = evaluation;
      }

      const possibleDisclosure = getProposalDisclosure(
        draft,
        evaluableId,
        member.id,
      );
      const isRecused = possibleDisclosure?.decision === "recused";

      const throwIfNotLegacy = (msg: string) => {
        if (!action.legacy) {
          throw new Error(msg);
        }
      };

      if ((action.change === "defer") === (action.deferredTo === undefined)) {
        throw new Error("deferredTo must be set iff change === 'defer'");
      }

      if (action.change === "start") {
        if (!["not-started", "hidden", "canceled"].includes(evaluation.state)) {
          throwIfNotLegacy(
            "Evaluation must be in the not-started, hidden, or canceled state to start",
          );
        }
        evaluation.state = "evaluated";
        evaluation.hidden = false;
        ensureRecommenderEvaluationHasMvf(evaluator, evaluableId);
        // (Re)starting retracts a finality verdict only when the resulting
        // curve actually contradicts it — the same rule as curve edits and
        // un-recusals. Two exemptions: a restart that keeps a
        // zero/at-or-below-hold curve leaves the coherent verdict standing,
        // and an undo restore (meta.undoes set — undoing a cancellation)
        // never retracts, so the inverse faithfully reproduces the
        // pre-cancel state even when a hold-line move left the verdict
        // beside a meaningful curve (which cancel deliberately preserved).
        // `undoes` is persisted and replayed verbatim, so this is
        // deterministic.
        if (
          meta.undoes == null &&
          evaluation.finality != null &&
          mvfIsMeaningfullyValued(
            evaluator.mvfProfile[evaluableId],
            getHoldValue(evaluator.mvfProfile, draft.holdProposalId),
          )
        ) {
          evaluation.finality = undefined;
        }
      } else if (action.change === "cancel") {
        if (evaluation.state !== "evaluated") {
          throwIfNotLegacy(
            "Evaluation must be in the evaluated state to cancel",
          );
        }
        if (isRecused) {
          throwIfNotLegacy("Can't cancel a recused evaluation");
        }
        evaluation.state = "canceled";
      } else if (action.change === "recuse") {
        if (evaluation.state !== "deferred") {
          throwIfNotLegacy(
            "Evaluation must be in the deferred state to recuse",
          );
        }
        if (!isRecused) {
          throwIfNotLegacy("Can't recuse an evaluation if you're not recused");
        }
        evaluation.state = "recused";
        evaluation.deferredTo = undefined;
      } else if (action.change === "defer") {
        if (evaluation.state !== "recused") {
          throwIfNotLegacy("Evaluation must be in the recused state to defer");
        }
        if (!isRecused) {
          throwIfNotLegacy("Can't defer an evaluation if you're not recused");
        }
        evaluation.state = "deferred";
        evaluation.deferredTo = action.deferredTo;
      }
      evaluation.lastTouchedAt = action.timestamp;
      break;
    }
    case "NORMALIZE_EVALUATIONS": {
      const { evaluatorId } = action;
      const recommender = assertGetRecommender(draft, evaluatorId);
      if (action.restoreMvfProfile != null) {
        for (const [evaluableId, mvf] of Object.entries(
          action.restoreMvfProfile,
        )) {
          const validationResult = validatePoints(
            mvf,
            evaluableId === draft.holdProposalId,
          );
          if (validationResult.type === "error") {
            throw new Error(
              `Invalid MVF for ${evaluableId}: ${validationResult.message}`,
            );
          }
        }
        recommender.mvfProfile = action.restoreMvfProfile;
      } else {
        recommender.mvfProfile = normalizeMvfProfile(recommender.mvfProfile);
      }
      recommender.mvfChanges = (recommender.mvfChanges ?? 0) + 1;
      break;
    }
    case "PROPOSAL_FORM_FIELD_CHANGE": {
      const { proposalFormId, field } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      const fieldIndex = proposalForm.fields.findIndex(
        (f) => f.id === field.id,
      );
      if (fieldIndex === -1) {
        throw new Error(`Field with id ${field.id} not found`);
      }
      proposalForm.fields[fieldIndex] = field;
      break;
    }
    case "PROPOSAL_LONGNAME_CHANGE": {
      const { proposalId, proposalFormId, longname } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      const proposal = assertGetProposal(proposalForm, proposalId);
      proposal.longname = longname;
      pushProposalInfoEffect(draft, sideEffects, draft.id, proposal);
      break;
    }
    case "PROPOSAL_SHORTNAME_CHANGE": {
      const { proposalId, shortname } = action;
      const proposal = getProposal(draft, proposalId);
      if (!proposal) {
        throw new Error("Failed to find proposal");
      }
      proposal.shortname = shortname;
      pushProposalInfoEffect(draft, sideEffects, draft.id, proposal);
      break;
    }
    case "PROPOSAL_FIELD_RESPONSE_CHANGE": {
      const {
        proposalId,
        fieldId,
        response,
        beforeFieldResponsesId,
        afterFieldResponsesId,
      } = action;
      const proposal = getProposal(draft, proposalId);
      if (!proposal) {
        throw new Error("Proposal not found");
      }
      // The aggregate only tracks the responses *version*; the bodies live in
      // the info store, which merges this edit onto the version the client
      // edited when it applies the intent (see resolveIntentEffects). The
      // reducer itself reads nothing.
      proposal.fieldResponsesId = afterFieldResponsesId;
      const proposalForm = getProposalFormOfProposalId(draft, proposalId);
      sideEffects.push({
        type: "PATCH_FIELD_RESPONSE",
        roundId: draft.id,
        proposalId,
        fieldId,
        response,
        beforeFieldResponsesId,
        afterFieldResponsesId,
        actionId: meta.actionId,
        ...(proposalForm && proposal.id !== draft.holdProposalId
          ? {
              proposalInfo: {
                fields: plainFields(proposalForm.fields),
                proposal: proposalInfoFields(proposal),
              },
            }
          : {}),
      });
      break;
    }
    case "PROPOSAL_FIELD_TITLE_CHANGE": {
      const { proposalFormId, fieldId, fieldTitle } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      const field = assertGetField(proposalForm, fieldId);
      field.title = fieldTitle;
      // Retitling can change which fields count as "Plan for impact", so the
      // denormalized proposal info is re-derived — by the info store, which
      // holds the responses (the reducer reads nothing; proposals it has no
      // responses for are skipped there, as before).
      sideEffects.push({
        type: "REDERIVE_PROPOSAL_INFO",
        roundId: draft.id,
        fields: plainFields(proposalForm.fields),
        proposals: Object.values(proposalForm.proposals)
          .filter((proposal) => proposal.id !== draft.holdProposalId)
          .map(proposalInfoFields),
      });
      break;
    }
    case "PROPOSAL_FIELD_DESCRIPTION_CHANGE": {
      const { proposalFormId, fieldId, fieldDescription } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      const field = assertGetField(proposalForm, fieldId);
      field.description = fieldDescription;
      break;
    }
    case "PROPOSAL_FORM_EXTERNAL_SYNC": {
      const proposalForm = assertGetProposalForm(draft, action.proposalFormId);

      for (const proposal of Object.values(action.proposals)) {
        const fieldResponses = proposal.fieldResponses ?? {};
        sideEffects.push({
          type: "WRITE_FIELD_RESPONSES",
          roundId: draft.id,
          proposalId: proposal.id,
          fieldResponses,
        });
        proposalForm.proposals[proposal.id] = {
          ...omit(proposal, "fieldResponses"),
          fieldResponsesId: getFieldResponsesId(fieldResponses),
        };
        ensureProposalInProposalMemberAxes(draft, proposal.id);
      }

      for (const field of action.fields) {
        const fieldIndex = proposalForm.fields.findIndex(
          (f) => f.id === field.id,
        );
        if (fieldIndex === -1) {
          proposalForm.fields.push(field);
        } else {
          proposalForm.fields[fieldIndex] = field;
        }
      }

      // Emit proposal info after the field definitions have been synced so
      // planForImpact is derived against up-to-date field titles.
      for (const proposal of Object.values(action.proposals)) {
        const storedProposal = proposalForm.proposals[proposal.id];
        if (!storedProposal) continue;
        pushProposalInfoEffect(draft, sideEffects, draft.id, storedProposal, {
          fields: proposalForm.fields,
          fieldResponses: proposal.fieldResponses ?? {},
        });
      }
      break;
    }
    case "SET_TRACK_SETTINGS": {
      const { track, status, evaluationsDueDate } = action;
      draft.trackSettings[track].status = status;
      if (evaluationsDueDate) {
        draft.trackSettings[track].evaluationsDueDate = evaluationsDueDate;
      }
      break;
    }
    case "INVESTIGATION_INTEREST_CHANGE": {
      const { proposalId, memberId, interest, confirmedReviewer } = action;

      if (interest != null && (interest < 0 || interest > 4)) {
        throw new Error("Interest must be between 0 and 4 (inclusive)");
      }

      setInvestigation(draft, proposalId, memberId, {
        interest,
        confirmedReviewer,
      });
      break;
    }
    case "REVIEW_PROGRESS_CHANGE": {
      const { proposalId, memberId, progress } = action;
      if (progress != null && (progress < 1 || progress > 4)) {
        throw new Error("Progress must be between 1 and 4 (inclusive)");
      }
      const proposal = getProposal(draft, proposalId);
      if (!proposal) {
        throw new Error("Proposal not found");
      }
      setReviewProgress(draft, proposalId, memberId, progress);
      break;
    }
    case "PROPOSAL_DISCLOSURE_CHANGE": {
      const { proposalId, userId, hasDisclosure, selfRecused, disclosure } =
        action;
      if (!hasDisclosure && selfRecused) {
        throw new Error("Self-recusal requires an active disclosure");
      }

      const proposalDisclosure = getOrCreateProposalDisclosure(
        draft,
        proposalId,
        userId,
        () => ({
          hasDisclosure,
          selfRecused,
          disclosure,
          votes: {},
          decision: null,
        }),
      );
      proposalDisclosure.hasDisclosure = hasDisclosure;
      proposalDisclosure.selfRecused = selfRecused;
      proposalDisclosure.disclosure = disclosure;
      const userMember = assertGetMember(draft, userId);

      const { decision } = recusalDecision(
        recusalVoters(
          Object.values(draft.members),
          userMember,
          userMember.track,
        ),
        proposalDisclosure,
      );
      proposalDisclosure.decision = decision;

      possiblyUpdateEvaluationBasedOnRecusalVote(
        userMember,
        proposalId,
        decision,
        action.timestamp,
        draft.holdProposalId,
      );
      break;
    }
    case "RECUSAL_VOTE": {
      const { proposalId, votingUserId, subjectUserId, vote } = action;
      const proposal = getProposal(draft, proposalId);
      if (!proposal || proposal.status !== "active") {
        break;
      }

      const disclosure = getProposalDisclosure(
        draft,
        proposalId,
        subjectUserId,
      );
      if (!disclosure) {
        if (!action.legacy) {
          throw new Error("Can't vote on a non-disclosure");
        }
        break;
      }
      if (!disclosure.hasDisclosure) {
        if (!action.legacy) {
          throw new Error("Can't vote on a non-disclosure");
        }
      }
      disclosure.votes[votingUserId] = vote;

      const votingMember = assertGetMember(draft, votingUserId);
      const voterTrack = votingMember.track;
      if (voterTrack === "NO_TRACK") {
        throw new Error("User must be in a track to vote on recusals");
      }

      const evaluatorMember = assertGetMember(draft, subjectUserId);
      const { decision } = recusalDecision(
        recusalVoters(
          Object.values(draft.members),
          evaluatorMember,
          voterTrack,
        ),
        disclosure,
      );
      disclosure.decision = decision;

      possiblyUpdateEvaluationBasedOnRecusalVote(
        evaluatorMember,
        proposalId,
        decision,
        action.timestamp,
        draft.holdProposalId,
      );

      break;
    }
    case "ONE_TIME_ADMIN_ACTION_1": {
      const sffRollingApplicationFormId = "form-rOayziKPN_";
      const affectedProposals: {
        badCurrentProposalId: string;
        goodOriginalProposalId: string;
      }[] = [
        {
          // Effective Institutions Project
          badCurrentProposalId: "p-kn4gwcC36A",
          goodOriginalProposalId: "p-eRe3M_KEnO",
        },
        {
          // Ulyssean PBC
          badCurrentProposalId: "p-TYDrnl-15Q",
          goodOriginalProposalId: "p-X0DnZuWpDC",
        },
        {
          // Halcyon Futures Inc
          badCurrentProposalId: "p-aTcx5GR6hB",
          goodOriginalProposalId: "p-pwbiljchsd",
        },
        {
          // FAR AI
          badCurrentProposalId: "p-mGLGrFWMcZ",
          goodOriginalProposalId: "p-18ZD6FWNtk",
        },
      ];

      const proposalForm = assertGetProposalForm(
        draft,
        sffRollingApplicationFormId,
      );
      for (const {
        badCurrentProposalId,
        goodOriginalProposalId,
      } of affectedProposals) {
        const proposal = assertGetProposal(proposalForm, badCurrentProposalId);
        proposal.id = goodOriginalProposalId;
        delete proposalForm.proposals[badCurrentProposalId];
        proposalForm.proposals[goodOriginalProposalId] = proposal;
      }
      break;
    }
    case "ONE_TIME_ADMIN_ACTION_2": {
      const recommender = assertGetRecommender(draft, "rec-XhOqjaff");
      const idsToRemove = [
        "p-kn4gwcC36A",
        "p-TYDrnl-15Q",
        "p-aTcx5GR6hB",
        "p-mGLGrFWMcZ",
      ];
      delete recommender.evaluations["p-kn4gwcC36A"];
      for (const id of idsToRemove) {
        delete recommender.evaluations[id];
        delete recommender.mvfProfile[id];
      }
      break;
    }
    case "MATCHING_ADJUSTMENT_INTENSITY_CHANGE": {
      const { matchingAdjustmentIntensity } = action;
      // This legacy action was round-wide, so preserve its historical meaning
      // for both current funders and funders created later during replay.
      draft.deprecatedMatchingAdjustmentIntensity = matchingAdjustmentIntensity;
      for (const funder of getAllFunders(draft)) {
        funder.matchingBudgetRatio = matchingAdjustmentIntensity;
      }
      break;
    }
    case "FUNDER_MATCHING_BUDGET_RATIO_CHANGE": {
      const { funderId, matchingBudgetRatio } = action;
      const funder = assertGetFunder(draft, funderId);
      funder.matchingBudgetRatio = matchingBudgetRatio;
      break;
    }
    case "FUNDER_USES_MATCHING_CHANGE": {
      const { funderId, usesMatching } = action;
      const funder = assertGetFunder(draft, funderId);
      funder.usesMatching = usesMatching;
      break;
    }
    case "RESET_FUNDER_INCREMENTAL_EVALS": {
      const { funderId } = action;
      const funder = assertGetEvaluator(draft, funderId);
      if (funder.type !== "funder") {
        throw new Error("Funder not found");
      }
      funder.incrementalEvalSteps = [];
      funder.undoneIncrementalEvalSteps = [];
      funder.budget = 10_000_000;
      break;
    }
    case "CREATE_ROUND_WIZARD": {
      if (!meta.actingAsUserId) {
        throw new Error("CREATE_ROUND_WIZARD requires an acting user");
      }
      const userId = meta.actingAsUserId;
      const { round, proposal, members } = action;

      if (!members.some((member) => member.roles.includes("funder"))) {
        throw new Error("Round must have at least one funder");
      }

      if (
        members.some(
          (member) => member.id === userId && !member.roles.includes("admin"),
        )
      ) {
        throw new Error("User must be an admin");
      }

      // Add proposal form custom fields
      proposal?.customFields.forEach((field) => {
        applyAction(
          draft,
          {
            type: "ADD_OR_UPDATE_PROPOSAL_FORM_FIELD",
            proposalFormId: proposal.proposalFormId,
            field: {
              status: "active",
              type: "question",
              id: field.id,
              title: field.title,
              description: field.description,
              source: "appInterface",
            },
          },
          meta,
          sideEffects,
        );
      });

      if (proposal) {
        // Add proposal
        applyAction(
          draft,
          {
            type: "ADD_PROPOSAL",
            proposalFormId: proposal.proposalFormId,
            proposalId: proposal.proposalId,
            entityType: "for-profit",
            proposalFirstSubmittedDatetime: action.timestamp,
            proposalShortname: proposal.shortname,
            proposalLongname: proposal.longname,
            baseRequestAmount: proposal.requestAmount,
            fieldResponses: proposal.customFields.reduce(
              (acc, field) => {
                if (field.value !== undefined) {
                  acc[field.id] = field.value;
                }
                return acc;
              },
              {} as Record<string, string>,
            ),
          },
          meta,
          sideEffects,
        );
      }

      const budgetPerFunder =
        round.budget /
        members.filter((member) => member.roles.includes("funder")).length;

      sideEffects.push({
        type: "CREATE_ROUND",
        roundId: draft.id,
        name: round.name,
      });

      if (!members.some((member) => member.id === userId)) {
        members.push({
          id: userId,
          initials: "",
          roles: ["admin"],
          deanonymize: true,
        });
      }

      // Add round members
      // Note: CREATE_OR_UPDATE_ROUND_MEMBER already pushes ADD_ROUND_MEMBER side effect
      members.forEach((member) => {
        applyAction(
          draft,
          {
            type: "CREATE_OR_UPDATE_ROUND_MEMBER",
            // Members created by the wizard share the wizard action's
            // creation time.
            timestamp: action.timestamp,
            budget: budgetPerFunder,
            ...member,
          },
          meta,
          sideEffects,
        );
      });

      break;
    }
    case "CREATE_OR_UPDATE_ROUND_MEMBER": {
      const { actingAsUserId } = meta;
      const { id: roundMemberId, roles, budget, initials } = action;

      if (actingAsUserId === roundMemberId && !roles.includes("admin")) {
        throw new Error("User must be an admin");
      }

      const existingMember = draft.members[roundMemberId];

      if (
        existingMember &&
        !roles.includes("funder") &&
        getOtherFunders(draft, roundMemberId).length === 0
      ) {
        throw new Error("At least one funder is required");
      }

      const member: RoundMember = existingMember ?? {
        createdAt: action.timestamp,
        userId: roundMemberId,
        id: roundMemberId,
        initials,
        isRoundAdmin: false,
        status: "active",
        // Trackless rounds keep every recommender in MAIN (see usesTracks on
        // the round aggregate); only multi-track rounds park new members in the
        // NO_TRACK "unassigned" bucket until an admin assigns them.
        track: roundUsesTracks(draft) ? "NO_TRACK" : "MAIN",
        feedbackVisibleByDefault: true,
        evaluableFeedback: {},
        evaluableNotes: {},
        evaluablePermissions: {},
        evaluators: {},
        deanonymize: action.deanonymize ?? false,
      };

      Object.values(member.evaluators).forEach((evaluator) => {
        if (!roles.includes(evaluator.type)) {
          evaluator.status = "removed";
        }
      });

      roles.forEach((role) => {
        if (role === "admin") {
          return;
        }

        if (role === "funder" && !budget) {
          throw new Error("Budget is required for funders");
        }

        // deterministic id generation so that the action can be replayed
        const evaluatorId = roundMemberId.replace(
          /^u/,
          role === "funder" ? "f" : "rec",
        );

        let evaluator: Evaluator | undefined = member.evaluators[evaluatorId];

        const shortname = `${initials} (${role.charAt(0).toUpperCase()})`;
        if (shortname !== evaluator?.shortname) {
          assertShortnameIsUnique(draft, shortname);
        }

        if (evaluator) {
          evaluator.shortname = shortname;
          evaluator.status = "active";
        } else {
          evaluator = initEvaluator(
            evaluatorId,
            roundMemberId,
            shortname,
            role,
            draft.holdProposalId,
            undefined,
            !draft.isSfcRound,
          );
          applyDeprecatedMatchingAdjustment(draft, evaluator);

          member.evaluators[evaluatorId] = evaluator;
        }

        if (evaluator.type === "funder") {
          evaluator.budget = budget ?? 0;
        }
      });

      draft.members[roundMemberId] = {
        ...member,
        initials,
        isRoundAdmin: roles.includes("admin"),
        status: "active",
        deanonymize: action.deanonymize ?? member.deanonymize,
        isRestrictedFunder:
          action.isRestrictedFunder ?? member.isRestrictedFunder,
        hiddenRecommenderMemberIds:
          action.hiddenRecommenderMemberIds ??
          member.hiddenRecommenderMemberIds,
      };

      ensureMemberInProposalMemberAxes(draft, roundMemberId);

      sideEffects.push({
        type: "ADD_ROUND_MEMBER",
        roundId: draft.id,
        userId: roundMemberId,
        track: member.track,
        deanonymize: action.deanonymize ?? member.deanonymize,
      });

      break;
    }
    case "PROPOSAL_BASE_REQUEST_AMOUNT_CHANGE": {
      const { proposalFormId, proposalId, baseRequestAmount } = action;
      const proposalForm = assertGetProposalForm(draft, proposalFormId);
      const proposal = assertGetProposal(proposalForm, proposalId);
      proposal.typedFields.baseRequestAmount = baseRequestAmount;
      pushProposalInfoEffect(draft, sideEffects, draft.id, proposal);
      break;
    }
    case "ROUND_ADVANCED_ANALYTICS_CHANGE": {
      const { advancedAnalyticsEnabled } = action;
      draft.advancedAnalyticsEnabled = advancedAnalyticsEnabled;
      break;
    }
    case "IMPORT_PROPOSALS": {
      const { customFields, proposals } = action;

      Object.values(draft.proposalForms).forEach((proposalForm) => {
        customFields.forEach((customField) => {
          const existingCustomField = proposalForm.fields.find(
            (f) => f.id === customField.id,
          );

          if (existingCustomField) {
            existingCustomField.title = customField.title;
            existingCustomField.description = customField.description;

            if (
              existingCustomField.status !== "active" ||
              existingCustomField.type !== "question" ||
              existingCustomField.source !== "appInterface"
            ) {
              console.warn(
                `IMPORT_PROPOSALS: Custom field ${customField.id} is not active or is not a question or is not from the app interface`,
              );
            }
          } else {
            proposalForm.fields.push({
              status: "active",
              type: "question",
              id: customField.id,
              title: customField.title,
              description: customField.description,
              source: "appInterface",
            });
          }
        });
      });

      proposals.forEach((proposal) => {
        const proposalForm = assertGetProposalForm(
          draft,
          proposal.proposalFormId,
        );
        ensureProposalInProposalMemberAxes(draft, proposal.id);
        const existingProposal = proposalForm.proposals[proposal.id];

        if (existingProposal) {
          existingProposal.shortname = proposal.shortname;
          existingProposal.longname = proposal.longname;
          existingProposal.typedFields.baseRequestAmount =
            proposal.requestAmount;

          // An import replaces the responses wholesale (WRITE_FIELD_RESPONSES
          // overwrites rather than merges).
          sideEffects.push({
            type: "WRITE_FIELD_RESPONSES",
            roundId: draft.id,
            proposalId: proposal.id,
            fieldResponses: proposal.fieldResponses,
          });
          existingProposal.fieldResponsesId = getFieldResponsesId(
            proposal.fieldResponses,
          );
          pushProposalInfoEffect(
            draft,
            sideEffects,
            draft.id,
            existingProposal,
            {
              fields: proposalForm.fields,
              fieldResponses: proposal.fieldResponses,
            },
          );
        } else {
          applyAction(
            draft,
            {
              type: "ADD_PROPOSAL",
              proposalFormId: proposal.proposalFormId,
              proposalId: proposal.id,
              entityType: "for-profit",
              // The import's creation time, not application-time wall clock —
              // a replayed import must produce the same submitted date.
              proposalFirstSubmittedDatetime: action.timestamp,
              proposalShortname: proposal.shortname,
              proposalLongname: proposal.longname,
              baseRequestAmount: proposal.requestAmount,
              fieldResponses: proposal.fieldResponses,
            },
            meta,
            sideEffects,
          );
        }
      });

      break;
    }
    case "NOOP":
      break;
    default:
      assertUnreachable(action);
  }
}

function getOtherFunders(draft: RoundAggregate, id: string) {
  return Object.values(draft.members).filter(
    (member) =>
      member.id !== id &&
      member.status === "active" &&
      Object.values(member.evaluators).some(
        (evaluator) =>
          evaluator.status === "active" && evaluator.type === "funder",
      ),
  );
}

function initializeRoundAggregate(
  action: ActionMessage,
  payload: AddRound,
): RoundAggregate {
  const { version, aggregateId } = action;
  // The round's creation time, carried in the payload (backfilled or injected
  // from the envelope for legacy rows) — never the envelope directly, so the
  // optimistic apply and the confirm re-apply produce identical aggregates.
  const timestamp = payload.timestamp;
  const {
    name,
    isSfcRound = true,
    usesTracks = true,
    roundIdentifier,
    userId,
    holdProposalId,
    holdProposalName,
    defaultProposalFormId,
    initials,
    track,
  } = payload;

  // Trackless rounds keep every member in MAIN and hide the track picker, so
  // ignore any non-MAIN track a caller (e.g. an older client) supplies rather
  // than persisting a hidden off-MAIN track the invariant forbids.
  const initialTrack = usesTracks ? track : "MAIN";

  const funderId = payload.initialFunderId;
  const recommenderId = payload.initialRecommenderId;
  // Legacy (v1/v2) rounds carry the identifier here; v3 rounds keep it on
  // their proposal forms and normally arrive without one.
  const normalizedRoundIdentifier = roundIdentifier?.trim() || undefined;

  const funderNode: Evaluator | null = funderId
    ? {
        ...initEvaluator(
          funderId,
          userId,
          `${initials} Funder`,
          "funder",
          holdProposalId,
        ),
        type: "funder",
        budget: 1_000_000,
      }
    : null;
  const proposalMemberIds = [...new Set([userId, meanUserId])];

  return {
    type: "Round",
    status: "active",
    trackSettings: {
      MAIN: {
        status: "1.onboarding",
        evaluationsDueDate: undefined,
      },
      FAIRNESS: {
        status: "1.onboarding",
        evaluationsDueDate: undefined,
      },
      FREEDOM: {
        status: "1.onboarding",
        evaluationsDueDate: undefined,
      },
    },
    id: aggregateId,
    version,
    latestActionId: action.id,
    name,
    isSfcRound,
    roundIdentifier: isSfcRound ? normalizedRoundIdentifier : undefined,
    advancedAnalyticsEnabled: false,
    usesTracks,
    holdProposalId: holdProposalId,
    members: {
      [userId]: {
        createdAt: timestamp,
        status: "active",
        id: userId,
        userId,
        isRoundAdmin: true,
        feedbackVisibleByDefault: true,
        track: initialTrack,
        initials,
        evaluableFeedback: {},
        evaluableNotes: {},
        evaluablePermissions: {},
        evaluators: {
          ...(funderNode ? { [funderId!]: funderNode } : {}),
          ...(recommenderId
            ? {
                [recommenderId]: initEvaluator(
                  recommenderId,
                  userId,
                  `${initials} Recommender`,
                  "recommender",
                  holdProposalId,
                ),
              }
            : {}),
        },
        deanonymize: payload.deanonymize ?? false,
      },
      [meanUserId]: {
        status: "active",
        createdAt: timestamp,
        id: meanUserId,
        userId: meanUserId,
        isRoundAdmin: false,
        track: initialTrack,
        initials: "Mean",
        feedbackVisibleByDefault: true,
        evaluableFeedback: {},
        evaluableNotes: {},
        evaluablePermissions: {},
        evaluators: {},
        deanonymize: true,
      },
    },
    proposalForms: {
      [defaultProposalFormId]: {
        id: defaultProposalFormId,
        status: "active",
        name: `${name} Default Form`,
        googleFormId: "",
        spreadsheetId: "",
        spreadsheetRange: "",
        proposals: {
          [holdProposalId]: {
            type: "proposal",
            status: "active",
            id: holdProposalId,
            shortname: holdProposalName,
            entityType: "non-profit",
            firstSubmittedDatetime: timestamp,
            longname: holdProposalName,
            lastSubmittedDatetime: timestamp,
            requestsMatchingPledge: false,
            typedFields: {
              baseRequestAmount: 0,
              receivingOrgName: "",
              receivingOrgTaxId: "",
              receivingOrgLocation: "",
              sffApplicationId: "",
              targetsFreedom: false,
              targetsFairness: false,
              longformLink: "http://example.com",
              keyIndividuals: [],
            },
            fieldResponsesId: getFieldResponsesId({}),
          },
        },
        fields: [],
      },
    },
    proposalMemberAxes: {
      proposalIds: [holdProposalId],
      memberIds: proposalMemberIds,
    },
    investigation: [Array(proposalMemberIds.length).fill(0)],
    reviewProgress: [Array(proposalMemberIds.length).fill(0)],
    proposalDisclosure: {},
  };
}

export type ActionSideEffects = {
  actionId: string;
  // Was the action already confirmed when we generated this side effect?
  actionAlreadyConfirmed: boolean;
  actionVersion: number;
  effects: SideEffect[];
};

export type ReducerResult =
  | {
      agg: RoundAggregate;
      sideEffects: ActionSideEffects;
    }
  | Error;

export type MultiReducerResult =
  | {
      agg: RoundAggregate;
      sideEffects: ActionSideEffects[];
    }
  | Error;

export class Reducer {
  performAction(
    agg: RoundAggregate | null,
    someActionMessage: IncomingActionMessage | IncomingUnconfirmedActionMessage,
  ): ReducerResult {
    const sideEffects: SideEffect[] = [];
    let unappliedAction: IncomingActionMessage;
    let actionAlreadyConfirmed: boolean;
    if (
      !("version" in someActionMessage) ||
      someActionMessage.version == null ||
      !("timestamp" in someActionMessage) ||
      !("ignoredReason" in someActionMessage)
    ) {
      // This is an UnappliedUnconfirmedActionMessage which already has: aggregateId, payload, userId, id
      actionAlreadyConfirmed = false;
      unappliedAction = {
        ...someActionMessage,
        ignoredReason: null,
        version: agg ? agg.version + 1 : 0,
        timestamp: new Date().toISOString(),
      };
    } else {
      // An ActionMessage is already an UnappliedActionMessage (plus patches and inversePatches)
      actionAlreadyConfirmed = true;
      unappliedAction = someActionMessage;
    }

    const parsedRemovedAction = removedAction.safeParse(
      unappliedAction.payload,
    );
    // StampableAction (timestamp possibly missing) until the fallback
    // injection below guarantees timestamped types carry one.
    let currentPayload: StampableAction;
    let translatedFromMsg = "";
    if (parsedRemovedAction.success) {
      // Historical payloads are normalized into the current schema before
      // permission checks and reducer application.
      currentPayload = upcastAction(parsedRemovedAction.data);
      translatedFromMsg = ` translated from ${unappliedAction.payload.type}`;
    } else {
      currentPayload = unappliedAction.payload;
    }

    const upcastedPayload = upcastCurrentAction(currentPayload);
    if (upcastedPayload !== currentPayload) {
      currentPayload = upcastedPayload;
      translatedFromMsg = `${translatedFromMsg} translated from CREATE_ROUND v1`;
    }

    // Fallback: a timestamped payload missing its creation time takes the
    // envelope timestamp. Reached only by persisted rows the backfill missed,
    // in-flight actions from stale clients, and upcast-synthesized payloads —
    // for all of which the envelope value is exactly what application
    // historically used, so replay output is unchanged.
    if (TIMESTAMPED_ACTION_TYPES.has(currentPayload.type)) {
      const stamped = currentPayload as { timestamp?: string };
      if (stamped.timestamp == null) {
        currentPayload = {
          ...currentPayload,
          timestamp: unappliedAction.timestamp,
        } as StampableAction;
      }
    }

    const translatedAction: ActionMessage = {
      ...unappliedAction,
      // Timestamped types are guaranteed stamped by the fallback above.
      payload: currentPayload as CurrentAction,
    };

    if (agg == null) {
      const action_ = translatedAction.payload;
      if (action_.type === "CREATE_ROUND") {
        agg = initializeRoundAggregate(translatedAction, action_);
      } else if (action_.type === "CREATE_ROUND_WIZARD") {
        if (!unappliedAction.actingAsUserId) {
          return new Error("CREATE_ROUND_WIZARD requires an acting user");
        }
        const { round, proposal } = action_;
        const actingMemberDeanonymize =
          action_.members.find(
            (member) => member.id === unappliedAction.actingAsUserId,
          )?.deanonymize ?? false;
        agg = initializeRoundAggregate(translatedAction, {
          type: "CREATE_ROUND",
          timestamp: action_.timestamp,
          version: 3,
          name: round.name,
          isSfcRound: round.isSfcRound,
          usesTracks: round.usesTracks,
          roundIdentifier: round.roundIdentifier,
          // Read the ids the client recorded in the event, so replaying this
          // event always rebuilds the same round. Legacy events predate these
          // fields; derive a stable id from the round id for them rather than
          // minting a fresh (per-replay) one. Never generate here.
          defaultProposalFormId:
            proposal?.proposalFormId ??
            round.defaultProposalFormId ??
            deriveLegacyProposalFormId(unappliedAction.aggregateId),
          holdProposalId:
            round.holdProposalId ??
            deriveLegacyProposalId(unappliedAction.aggregateId),
          holdProposalName: "Hold",
          userId: unappliedAction.actingAsUserId,
          initials: round.initials,
          track: "MAIN",
          deanonymize: actingMemberDeanonymize,
        });
      } else {
        return new Error(
          `Aggregate ${unappliedAction.aggregateId} not found when applying ${unappliedAction.payload.type}`,
        );
      }
    }

    if (unappliedAction.ignoredReason != null) {
      const newAgg = create(agg, (draft) => {
        draft.version = unappliedAction.version;
        draft.latestActionId = unappliedAction.id;
        return draft;
      });
      return {
        agg: newAgg,
        sideEffects: {
          actionId: unappliedAction.id,
          actionAlreadyConfirmed,
          actionVersion: unappliedAction.version,
          effects: [],
        },
      };
    }

    // An archived round is read-only. That is a property of the round's
    // state, not of who is acting, so it is checked here, ahead of the actor
    // fork below: SFC-admin dispatches (which skip isActionPermitted),
    // actor-less server jobs, agent tokens, and replay all pass through this
    // line. Only the restore itself gets through.
    if (
      isRoundArchived(agg) &&
      translatedAction.payload.type !== "ROUND_ARCHIVED_CHANGE"
    ) {
      return new ActionNotPermittedError(
        `Not permitted (${currentPayload.type}${translatedFromMsg}): ${ARCHIVED_ROUND_ERROR}`,
      );
    }

    // Actions marked as created by an SFC admin bypass engine permission
    // checks because authorization should already be enforced upstream.
    if (!unappliedAction.createdBySfcAdmin) {
      const actingAsUserId = translatedAction.actingAsUserId;
      // User actions require a human actor.
      if (!actingAsUserId) {
        return new Error(
          `Not permitted (${currentPayload.type}${translatedFromMsg}): Action requires a user`,
        );
      }

      // Check whether the user is allowed to perform this action.
      try {
        const message = isActionPermitted(
          agg,
          translatedAction.payload,
          actingAsUserId,
          { actionAlreadyConfirmed },
        );
        if (message !== true) {
          return new ActionNotPermittedError(
            `Not permitted (${currentPayload.type}${translatedFromMsg}): ${message}`,
          );
        }
      } catch (e) {
        console.error(e);
        return new Error(
          `Failed to apply action ${unappliedAction.id} (${currentPayload.type}${translatedFromMsg})`,
          { cause: e },
        );
      }
    }

    try {
      const newAgg = create(agg, (draft) => {
        applyAction(
          draft,
          translatedAction.payload,
          {
            actionId: translatedAction.id,
            actingAsUserId: translatedAction.actingAsUserId,
            undoes: translatedAction.undoes ?? null,
          },
          sideEffects,
        );
        draft.version = translatedAction.version;
        draft.latestActionId = translatedAction.id;
        return draft;
      });
      return {
        agg: newAgg,
        sideEffects: {
          actionId: translatedAction.id,
          actionAlreadyConfirmed,
          actionVersion: translatedAction.version,
          effects: sideEffects,
        },
      };
    } catch (e) {
      console.error(e);
      return new Error(
        `Failed to apply action ${unappliedAction.id} (${currentPayload.type}${translatedFromMsg})`,
        { cause: e },
      );
    }
  }

  runActionsOnAggregate(
    actions: (IncomingUnconfirmedActionMessage | IncomingActionMessage)[],
    agg: RoundAggregate | null = null,
  ): MultiReducerResult {
    const sideEffects: ActionSideEffects[] = [];
    for (const actionMessage of actions) {
      const result = this.performAction(agg, actionMessage);

      if (result instanceof Error) {
        return result;
      }

      sideEffects.push(result.sideEffects);
      agg = result.agg;
    }

    if (agg == null) {
      return new Error("Didn't reduce any actions");
    }

    return { agg, sideEffects };
  }
}

export const roundReducer = new Reducer();

export type RoundSchema = CurrentAction;

// Notes/feedback can arrive before the recommender has started evaluating
// (e.g. via the agent-token API, which never dispatches START_EVALUATION), so
// create a not-started evaluation to carry lastTouchedAt — the timestamp the
// UI shows next to the note. Only create it for a proposal that exists in the
// round: evaluableId isn't validated upstream, and the consistency check
// rejects evaluations of unknown evaluables.
function touchRecommenderEvaluation(
  draft: RoundAggregate,
  member: RoundMember,
  evaluableId: string,
  timestamp: string,
) {
  // A removed recommender can precede its active replacement in
  // member.evaluators; the UI reads the active one, so stamp that.
  const evaluator = Object.values(member.evaluators).find(
    (e): e is Recommender => e.type === "recommender" && e.status === "active",
  );
  if (!evaluator) {
    return;
  }
  const evaluation = evaluator.evaluations[evaluableId];
  if (evaluation) {
    evaluation.lastTouchedAt = timestamp;
  } else if (getEvaluable(draft, evaluableId)?.type === "proposal") {
    evaluator.evaluations[evaluableId] = {
      evaluableId,
      evaluableType: "proposal",
      state: "not-started",
      hidden: false,
      lastTouchedAt: timestamp,
    };
  }
}

function possiblyUpdateEvaluationBasedOnRecusalVote(
  member: RoundMember,
  proposalId: string,
  decision: "recused" | "not-recused" | null,
  timestamp: string,
  holdProposalId: string,
) {
  for (const evaluator of Object.values(member.evaluators)) {
    if (evaluator.type === "recommender") {
      const evaluation = evaluator.evaluations[proposalId];
      const currentState = evaluation?.state;

      if (decision === "recused") {
        if (evaluation == null) {
          evaluator.evaluations[proposalId] = {
            evaluableId: proposalId,
            evaluableType: "proposal",
            state: "recused",
            deferredTo: undefined,
            hidden: false,
            lastTouchedAt: timestamp,
          };
        } else if (
          currentState != null &&
          ["not-started", "evaluated", "canceled", "hidden"].includes(
            currentState,
          )
        ) {
          evaluation.state = evaluation.deferredTo ? "deferred" : "recused";
          evaluation.lastTouchedAt = timestamp;
        }
      } else {
        if (
          evaluation &&
          currentState != null &&
          ["recused", "deferred"].includes(currentState)
        ) {
          evaluation.state = "evaluated";
          evaluation.lastTouchedAt = timestamp;
          ensureRecommenderEvaluationHasMvf(evaluator, proposalId);
          // Lifting a recusal reactivates the evaluation, usually installing
          // the default (meaningfully valued) curve just above. A standing
          // finality verdict would then contradict an active valued
          // evaluation, so retract it — but keep it when the reactivated
          // curve is still zero/at-or-below hold, where the statement remains
          // coherent (mirrors EVALUATION_MVF_CHANGE's conditional clear).
          if (
            evaluation.finality != null &&
            mvfIsMeaningfullyValued(
              evaluator.mvfProfile[proposalId],
              getHoldValue(evaluator.mvfProfile, holdProposalId),
            )
          ) {
            evaluation.finality = undefined;
          }
        }
      }
    }
  }
}
